// Paint configuration model — soporta 3 modos:
//   solid       → un color fijo
//   categorical → match por valor (string column)
//   gradient    → interpolate linear (numeric column)
//
// Cada modo se serializa a expresiones MapLibre estándar para el campo
// fill-color / line-color / circle-color según el geometryType.
import type { Layer } from './api';

export type PaintMode = 'solid' | 'categorical' | 'gradient';

export type PaintConfig = {
  mode: PaintMode;
  // solid
  color?: string;
  // categorical
  byColumn?: string;
  categories?: { value: string; color: string }[];
  defaultColor?: string;
  // gradient
  byNumeric?: string;
  rampMin?: number;
  rampMax?: number;
  rampColors?: [string, string];   // [low, high]
  // shared
  opacity?: number;
  outlineColor?: string;
  width?: number;          // line / circle stroke
  radius?: number;         // circle radius
};

export type GeomKind = 'fill' | 'line' | 'circle';

export function geomKind(layerType: Layer['geometryType']): GeomKind {
  if (layerType.includes('Polygon')) return 'fill';
  if (layerType.includes('Line')) return 'line';
  return 'circle';
}

// Lee un PaintConfig desde el JSON guardado en `layer.paint`.
// Backwards-compatible: un paint plano viejo (sólo {fill-color, fill-opacity}) → mode 'solid'.
export function readPaintConfig(layer: Layer): PaintConfig {
  const p = layer.paint as Record<string, any>;
  const mode = (p.__mode as PaintMode | undefined) ?? 'solid';
  const kind = geomKind(layer.geometryType);
  const colorKey = `${kind}-color`;
  return {
    mode,
    color: typeof p[colorKey] === 'string' ? p[colorKey] : '#0ea5e9',
    byColumn: p.__byColumn,
    categories: p.__categories ?? [],
    defaultColor: p.__defaultColor ?? '#94a3b8',
    byNumeric: p.__byNumeric,
    rampMin: p.__rampMin,
    rampMax: p.__rampMax,
    rampColors: p.__rampColors ?? ['#fef3c7', '#dc2626'],
    opacity: p[`${kind}-opacity`] ?? (kind === 'fill' ? 0.5 : 1),
    outlineColor: p[`${kind === 'circle' ? 'circle-stroke-color' : kind === 'fill' ? 'fill-outline-color' : 'line-color'}`] ?? '#0c4a6e',
    width: p['line-width'] ?? p['circle-stroke-width'] ?? 1,
    radius: p['circle-radius'] ?? 5,
  };
}

// Convierte un PaintConfig al JSON serializable que se guarda en `layer.paint`.
// Incluye expresiones MapLibre + metadatos `__mode`, `__byColumn`, etc para round-trip.
export function buildPaintJson(layer: Layer, cfg: PaintConfig): Record<string, unknown> {
  const kind = geomKind(layer.geometryType);
  const out: Record<string, unknown> = { __mode: cfg.mode };

  // Color principal según el modo
  let color: unknown;
  if (cfg.mode === 'categorical' && cfg.byColumn && cfg.categories?.length) {
    out.__byColumn = cfg.byColumn;
    out.__categories = cfg.categories;
    out.__defaultColor = cfg.defaultColor ?? '#94a3b8';
    color = ['match', ['get', cfg.byColumn],
      ...cfg.categories.flatMap(c => [c.value, c.color]),
      cfg.defaultColor ?? '#94a3b8',
    ];
  } else if (cfg.mode === 'gradient' && cfg.byNumeric && cfg.rampColors) {
    out.__byNumeric = cfg.byNumeric;
    out.__rampMin = cfg.rampMin ?? 0;
    out.__rampMax = cfg.rampMax ?? 1;
    out.__rampColors = cfg.rampColors;
    color = ['interpolate', ['linear'], ['coalesce', ['to-number', ['get', cfg.byNumeric]], cfg.rampMin ?? 0],
      cfg.rampMin ?? 0, cfg.rampColors[0],
      cfg.rampMax ?? 1, cfg.rampColors[1],
    ];
  } else {
    color = cfg.color ?? '#0ea5e9';
  }

  out[`${kind}-color`] = color;
  out[`${kind}-opacity`] = cfg.opacity ?? (kind === 'fill' ? 0.5 : 1);

  if (kind === 'fill') {
    out['fill-outline-color'] = cfg.outlineColor ?? '#0c4a6e';
  } else if (kind === 'line') {
    out['line-width'] = cfg.width ?? 2;
  } else {
    out['circle-radius'] = cfg.radius ?? 5;
    out['circle-stroke-color'] = cfg.outlineColor ?? '#0c4a6e';
    out['circle-stroke-width'] = cfg.width ?? 1;
  }

  return out;
}

// Quita propiedades `__*` (metadatos UI) antes de pasar a MapLibre. MapLibre v4
// emite errors de validación cuando ve propiedades de paint desconocidas y eso
// ha causado que algunas capas no se rendericen. Esto deja sólo las llaves
// "fill-*", "line-*", "circle-*" que MapLibre conoce.
export function cleanPaintForMapLibre(paint: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(paint)) {
    if (!k.startsWith('__')) out[k] = v;
  }
  return out;
}

// Genera entradas de leyenda a partir de un layer (label + color sample).
export function legendItems(layer: Layer): { label: string; color: string }[] {
  const cfg = readPaintConfig(layer);
  if (cfg.mode === 'categorical' && cfg.categories?.length) {
    return [
      ...cfg.categories.map(c => ({ label: c.value, color: c.color })),
      { label: 'otros', color: cfg.defaultColor ?? '#94a3b8' },
    ];
  }
  if (cfg.mode === 'gradient' && cfg.rampColors) {
    return [
      { label: `≤ ${cfg.rampMin ?? 0}`, color: cfg.rampColors[0] },
      { label: `≥ ${cfg.rampMax ?? 1}`, color: cfg.rampColors[1] },
    ];
  }
  return [{ label: layer.name, color: cfg.color ?? '#0ea5e9' }];
}
